import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import SearchableSelect from "../../components/common/SearchableSelect";
import { warningAlert, errorAlert, successAlert } from "../../utils/alert";
import PDFViewer from "../../components/common/PDFViewer";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaClipboardList,
  FaIdCard,
  FaListUl,
  FaMedal,
  FaProjectDiagram,
  FaRandom,
  FaStopwatch,
  FaUsers,
} from "react-icons/fa";

const GameDay = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(searchParams.get("date") || new Date().toISOString().slice(0, 10));
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [classificationModalOpen, setClassificationModalOpen] = useState(false);
  const [pendingClassifications, setPendingClassifications] = useState([]);
  const [classOptionsBySport, setClassOptionsBySport] = useState({});
  const [selectedClassByReg, setSelectedClassByReg] = useState({});
  const [pendingTargetPath, setPendingTargetPath] = useState("");
  const [savingClassifications, setSavingClassifications] = useState(false);
  const [gameDayGender, setGameDayGender] = useState(() => {
    const g = (searchParams.get("gender") || "all").toLowerCase();
    return g === "men" ? "male" : g === "women" ? "female" : g;
  });
  const activeGenderLabel =
    gameDayGender === "male" || gameDayGender === "men"
      ? "Male"
      : gameDayGender === "female" || gameDayGender === "women"
      ? "Female"
      : "All";
  const buildContextQuery = (eventIdOverride = selectedEventId, extra = {}) => {
    const q = new URLSearchParams();
    if (date) q.set("date", String(date));
    if (eventIdOverride) q.set("event_id", String(eventIdOverride));
    q.set("gender", gameDayGender || "all");
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        q.set(key, String(value));
      }
    });
    return q.toString();
  };

  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(selectedEventId)) || null,
    [events, selectedEventId]
  );

  const isAthleticsEvent = useMemo(() => {
    const sport = String(selectedEvent?.sport_name || selectedEvent?.sport || "").toLowerCase();
    const title = String(selectedEvent?.title || "").toLowerCase();
    return sport.includes("athlet") || title.includes("athlet");
  }, [selectedEvent]);

  const normalizePendingRows = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const map = new Map();
    list.forEach((r) => {
      const disc = String(r.required_discipline || "general").toLowerCase();
      const key = `${Number(r.player_id)}-${Number(r.sport_id)}-${disc}`;
      if (!map.has(key)) {
        map.set(key, r);
      }
    });
    return Array.from(map.values());
  };

  const pendingRowKey = (row) =>
    `${Number(row?.player_id || 0)}-${Number(row?.sport_id || 0)}-${String(
      row?.required_discipline || "general"
    ).toLowerCase()}`;

  const loadEvents = async (targetDate = date) => {
    setLoading(true);
    setError("");
    try {
      const res = await API.fetchEventsByDate(targetDate);
      const list = res?.data || res || [];
      const normalizedList = Array.isArray(list) ? list : [];
      setEvents(normalizedList);
      const eventFromQuery = searchParams.get("event_id");
      if (!normalizedList.length) {
        setSelectedEventId("");
      } else if (eventFromQuery && normalizedList.some((e) => String(e.id) === String(eventFromQuery))) {
        setSelectedEventId(String(eventFromQuery));
      } else if (!normalizedList.some((e) => String(e.id) === String(selectedEventId))) {
        setSelectedEventId(String(normalizedList[0]?.id || ""));
      }
    } catch (err) {
      setError(err?.message || "Failed to load events");
      setEvents([]);
      setSelectedEventId("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const dateFromQuery = searchParams.get("date");
    const nextDate = dateFromQuery || date;
    const genderFromQuery = (searchParams.get("gender") || "").toLowerCase();
    if (genderFromQuery) {
      const normalized = genderFromQuery === "men" ? "male" : genderFromQuery === "women" ? "female" : genderFromQuery;
      if (normalized !== gameDayGender) setGameDayGender(normalized);
    }
    if (dateFromQuery && dateFromQuery !== date) {
      setDate(dateFromQuery);
    }
    loadEvents(nextDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const requireEventAndGo = async (builder) => {
    if (!selectedEventId) {
      warningAlert("Select an event first.");
      return;
    }

    try {
      const resp = await API.get(`/admin/results/event/${selectedEventId}/classification-needed`);
      const rows = resp?.data || resp || [];
      const list = normalizePendingRows(rows);
      if (!list.length) {
        navigate(builder(selectedEventId));
        return;
      }

      const uniqueSportDisciplinePairs = [
        ...new Set(
          list
            .map((r) => `${Number(r.sport_id)}::${String(r.required_discipline || "general").toLowerCase()}`)
            .filter((v) => v && !v.startsWith("0::"))
        ),
      ];
      const loaded = {};
      await Promise.all(
        uniqueSportDisciplinePairs.map(async (pair) => {
          const [sid, disc] = pair.split("::");
          const clsRes = await API.get(`/admin/classifications/sports/${sid}/classifications`, {
            params: { discipline: disc },
          });
          const cls = clsRes?.data || clsRes || [];
          loaded[pair] = Array.isArray(cls) ? cls : [];
        })
      );

      setClassOptionsBySport(loaded);
      setPendingClassifications(list);
      setSelectedClassByReg({});
      setPendingTargetPath(builder(selectedEventId));
      setClassificationModalOpen(true);
      warningAlert(
        "Classification Pending",
        "Please assign classification for all listed players before proceeding."
      );
    } catch (e) {
      errorAlert("Failed", "Unable to verify pending classifications.");
    }
  };

  const refreshPendingClassifications = async () => {
    const resp = await API.get(`/admin/results/event/${selectedEventId}/classification-needed`);
    const rows = resp?.data || resp || [];
    const list = normalizePendingRows(rows);
    setPendingClassifications(list);
    return list;
  };

  const saveClassifications = async ({ continueAfterSave = false } = {}) => {
    if (!pendingClassifications.length) {
      setClassificationModalOpen(false);
      if (continueAfterSave && pendingTargetPath) navigate(pendingTargetPath);
      return;
    }

    const missingSelection = pendingClassifications.filter(
      (r) => !selectedClassByReg[pendingRowKey(r)]
    );

    if (missingSelection.length) {
      warningAlert(
        "Selection Required",
        `Select classification for all pending players (${missingSelection.length} remaining).`
      );
      return;
    }

    try {
      setSavingClassifications(true);
      await Promise.all(
        pendingClassifications.map((r) =>
          API.post("/admin/classifications/assign", {
            player_id: Number(r.player_id),
            sport_id: Number(r.sport_id),
            classification_id: Number(selectedClassByReg[pendingRowKey(r)]),
          })
        )
      );

      const stillPending = await refreshPendingClassifications();
      if (stillPending.length) {
        successAlert("Saved", "Some players still need classification. Please complete remaining.");
        return;
      }

      successAlert("Completed", "All pending classifications saved.");
      setClassificationModalOpen(false);
      if (continueAfterSave && pendingTargetPath) {
        navigate(pendingTargetPath);
      }
    } catch (e) {
      errorAlert("Save Failed", e?.message || "Unable to save classifications.");
    } finally {
      setSavingClassifications(false);
    }
  };

  const downloadGameDayFile = async (path, fallbackName, format = "pdf") => {
    if (!selectedEventId) {
      warningAlert("Select an event first.");
      return;
    }

    try {
      const res = await API.get(path, {
        params: { gender: gameDayGender, format },
        responseType: "blob",
      });
      const blob = res?.data;
      if (!blob) throw new Error("Empty file response.");

      const disposition =
        res?.headers?.["content-disposition"] ||
        res?.headers?.["Content-Disposition"] ||
        "";
      const contentType =
        (
          res?.headers?.["content-type"] ||
          res?.headers?.["Content-Type"] ||
          blob?.type ||
          ""
        ).toLowerCase();

      let fileName = fallbackName;
      const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
      if (match?.[1]) {
        fileName = decodeURIComponent(match[1]);
      }
      if (format === "excel" && contentType.includes("text/csv")) {
        if (/\.xlsx$/i.test(fileName)) {
          fileName = fileName.replace(/\.xlsx$/i, ".csv");
        } else if (!/\.csv$/i.test(fileName)) {
          fileName = `${fileName}.csv`;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      errorAlert("Failed", e?.message || "Unable to download report.");
    }
  };

  const downloadAttendanceSheet = async (format = "pdf") => {
    await downloadGameDayFile(
      `/admin/game-day/attendance-sheet/${selectedEventId}`,
      `attendance_sheet_event_${selectedEventId}.${format === "excel" ? "xlsx" : "pdf"}`,
      format
    );
  };

  const downloadEventList = async (format = "pdf") => {
    await downloadGameDayFile(
      `/admin/game-day/event-list/${selectedEventId}`,
      `event_list_${selectedEventId}.${format === "excel" ? "xlsx" : "pdf"}`,
      format
    );
  };

  const downloadAthleticsBibCards = async (format = "pdf") => {
    await downloadGameDayFile(
      `/admin/game-day/athletics-bib-cards/${selectedEventId}`,
      `athletics_bib_cards_event_${selectedEventId}.${format === "excel" ? "xlsx" : "pdf"}`,
      format
    );
  };

  const printGameDayFile = async (path) => {
    if (!selectedEventId) {
      warningAlert("Select an event first.");
      return;
    }
    try {
        const res = await API.get(path, {
            params: { gender: gameDayGender, format: "pdf" },
            responseType: "blob",
        });
        const blob = res?.data;
        if (!blob) throw new Error("Empty file response.");
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        const url = window.URL.createObjectURL(pdfBlob);
        setPreviewUrl(url);
    } catch (e) {
        errorAlert("Failed", e?.message || "Unable to prepare document for printing.");
    }
  };

  const printAttendanceSheet = async () => {
    await printGameDayFile(`/admin/game-day/attendance-sheet/${selectedEventId}`);
  };

  const printEventList = async () => {
    await printGameDayFile(`/admin/game-day/event-list/${selectedEventId}`);
  };

  const printAthleticsBibCards = async () => {
    await printGameDayFile(`/admin/game-day/athletics-bib-cards/${selectedEventId}`);
  };

  const handlePrint = () => {
    const modalContent = document.getElementById('print-modal-content');
    if (!modalContent) return;

    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        body > *:not(.print-this) {
          display: none !important;
        }
        .print-this {
          display: block !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
        }
        .pdf-viewer-controls {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    modalContent.classList.add('print-this');

    window.print();

    // Cleanup
    modalContent.classList.remove('print-this');
    if (document.head.contains(style)) {
      document.head.removeChild(style);
    }
  };

  const workflowCards = [
    {
      key: "engine",
      title: "Game Engine",
      desc: "Generate heats and event fixtures",
      action: () => requireEventAndGo((id) => `/admin/game-engine?${buildContextQuery(id)}`),
      icon: FaProjectDiagram,
      tone: "from-cyan-500 to-blue-600",
    },
    {
      key: "schedule",
      title: "Schedule",
      desc: "Create final schedule by event",
      action: () => requireEventAndGo((id) => `/admin/schedule/${id}?${buildContextQuery(id)}`),
      icon: FaCalendarAlt,
      tone: "from-blue-500 to-indigo-600",
    },
    {
      key: "medals",
      title: "Medal Tally",
      desc: "Review district standings",
      action: () => requireEventAndGo((id) => `/admin/medal-tally/${id}?${buildContextQuery(id)}`),
      icon: FaMedal,
      tone: "from-amber-500 to-orange-600",
    },
    {
      key: "timed",
      title: "Timed Results",
      desc: "Capture and adjust time entries",
      action: () => requireEventAndGo((id) => `/admin/results/timed/${id}?${buildContextQuery(id)}`),
      icon: FaStopwatch,
      tone: "from-emerald-500 to-teal-600",
    },
    {
      key: "points",
      title: "Points Results",
      desc: "Capture attempts and points",
      action: () => requireEventAndGo((id) => `/admin/results/points/${id}?${buildContextQuery(id)}`),
      icon: FaClipboardList,
      tone: "from-purple-500 to-violet-600",
    },
    {
      key: "bracket",
      title: "Bracket",
      desc: "Manage knockout and round flow",
      action: () => requireEventAndGo((id) => `/admin/results/bracket/${id}?${buildContextQuery(id)}`),
      icon: FaRandom,
      tone: "from-fuchsia-500 to-pink-600",
    },
    {
      key: "team",
      title: "Team Manager",
      desc: "Create teams and map members",
      action: () => requireEventAndGo((id) => `/admin/results/team/${id}?${buildContextQuery(id)}`),
      icon: FaUsers,
      tone: "from-slate-600 to-slate-800",
    },
  ];

  const exportCards = [
    {
      key: "attendance",
      title: "Attendance Sheet",
      desc: "Download or print attendance register",
      icon: FaClipboardList,
      onPdf: () => downloadAttendanceSheet("pdf"),
      onExcel: () => downloadAttendanceSheet("excel"),
      onPrint: () => printAttendanceSheet(),
    },
    {
      key: "event-list",
      title: "Event List",
      desc: "Player/event listing with category details",
      icon: FaListUl,
      onPdf: () => downloadEventList("pdf"),
      onExcel: () => downloadEventList("excel"),
      onPrint: () => printEventList(),
    },
  ];

  if (isAthleticsEvent) {
    exportCards.push({
      key: "bib-cards",
      title: "Athletics BIB Cards",
      desc: "Generate bib cards for athletics participants",
      icon: FaIdCard,
      onPdf: () => downloadAthleticsBibCards("pdf"),
      onExcel: () => downloadAthleticsBibCards("excel"),
      onPrint: () => printAthleticsBibCards(),
    });
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
          <section className="mb-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-3 sm:p-3.5 shadow-sm">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Operations Center</p>
                <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5">Game Day Management</h1>
              </div>
              <button
                onClick={() => navigate(`/admin/game-day-2?${buildContextQuery()}`)}
                className="inline-flex items-center gap-2 text-xs sm:text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              >
                Open Game Day 2
                <FaArrowRight size={12} />
              </button>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Events</p>
                <p className="text-base font-semibold text-slate-900">{events.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Selected</p>
                <p className="text-sm font-semibold text-slate-900 truncate">{selectedEvent?.title || "None"}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Gender</p>
                <p className="text-base font-semibold text-cyan-800">{activeGenderLabel}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Athletics Mode</p>
                <p className="text-base font-semibold text-amber-800">{isAthleticsEvent ? "Enabled" : "Standard"}</p>
              </div>
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)]">
            <aside className="self-start rounded-3xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-24">
              <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-600">Context</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Event Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl p-2.5"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Selected Event</label>
                  <SearchableSelect
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl p-2.5"
                  >
                    <option value="">-- Select Event --</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title || `Event #${event.id}`}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Gender Filter</label>
                  <SearchableSelect
                    value={gameDayGender}
                    onChange={(e) => setGameDayGender(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl p-2.5"
                  >
                    <option value="all">All</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </SearchableSelect>
                </div>
                <button
                  onClick={() => loadEvents(date)}
                  disabled={loading}
                  className="w-full h-11 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? "Loading..." : "Refresh Event List"}
                </button>
                {error ? <p className="text-sm text-red-600">{error}</p> : null}
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Current Selection</p>
                <p className="text-sm font-semibold text-slate-900 mt-1">{selectedEvent?.title || "No event selected"}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedEvent?.location || "Location not set"}</p>
              </div>

              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Workflow Order</p>
                <ol className="mt-2 text-sm text-amber-900 space-y-1">
                  <li>1. Load event context</li>
                  <li>2. Print required packs</li>
                  <li>3. Open execution modules</li>
                </ol>
              </div>
            </aside>

            <div className="space-y-4">
              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3">
                  <h2 className="text-lg font-semibold text-slate-900">Download & Print Desk</h2>
                  <p className="text-sm text-slate-600">Generate ready-to-use documents for on-ground operations.</p>
                </div>
                <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
                  {exportCards.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.key} className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4">
                        <div className="flex items-center gap-2">
                          <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-700">
                            <Icon size={15} />
                          </span>
                          <p className="font-semibold text-slate-900">{item.title}</p>
                        </div>
                        <p className="text-sm text-slate-600 mt-2 min-h-[38px]">{item.desc}</p>
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <button
                            onClick={item.onPdf}
                            disabled={!selectedEventId}
                            className="h-9 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:bg-gray-300"
                          >
                            PDF
                          </button>
                          <button
                            onClick={item.onExcel}
                            disabled={!selectedEventId}
                            className="h-9 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:bg-gray-300"
                          >
                            Excel
                          </button>
                          <button
                            onClick={item.onPrint}
                            disabled={!selectedEventId}
                            className="h-9 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 disabled:bg-gray-300"
                          >
                            Print
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3">
                  <h2 className="text-lg font-semibold text-slate-900">Workflow Modules</h2>
                  <p className="text-sm text-slate-600">Jump to scheduling, tally and results with the same event context.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {workflowCards.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={item.action}
                        className="group relative rounded-2xl border border-slate-200 bg-white p-4 text-left"
                      >
                        <div className={`absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r ${item.tone}`} />
                        <div className="flex items-start justify-between gap-2">
                          <span className="inline-flex items-center gap-2">
                            <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-700">
                              <Icon size={14} />
                            </span>
                            <span>
                              <span className="block text-[11px] uppercase tracking-wide text-slate-500">Module {index + 1}</span>
                              <span className="block font-semibold text-slate-900">{item.title}</span>
                            </span>
                          </span>
                          <FaArrowRight className="text-slate-400 group-hover:text-slate-700" size={12} />
                        </div>
                        <p className="text-sm text-slate-600 mt-2 min-h-[38px]">{item.desc}</p>
                        <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-slate-700">
                          Open Module <FaArrowRight size={10} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {classificationModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Pending Classifications ({pendingClassifications.length})
              </h2>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setClassificationModalOpen(false)}
                disabled={savingClassifications}
              >
                Close
              </button>
            </div>

            <div className="p-4 overflow-auto max-h-[60vh]">
              <div className="space-y-3">
                {pendingClassifications.map((row) => {
                  const key = pendingRowKey(row);
                  const discipline = String(row.required_discipline || "general").toLowerCase();
                  const optKey = `${Number(row.sport_id)}::${discipline}`;
                  const options = classOptionsBySport[optKey] || [];
                  return (
                    <div key={key} className="border rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-900">{row.name}</div>
                      <div className="text-xs text-gray-600 mb-2">
                        {row.sport_name || "-"} | {row.game_name || "-"} | {row.district || "-"} | {discipline}
                      </div>
                      <select
                        value={selectedClassByReg[key] || ""}
                        onChange={(e) =>
                          setSelectedClassByReg((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                        disabled={savingClassifications}
                      >
                        <option value="">Select Classification</option>
                        {options.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.classification}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 border-t flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => saveClassifications({ continueAfterSave: false })}
                disabled={savingClassifications}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400"
              >
                {savingClassifications ? "Saving..." : "Save Classifications"}
              </button>
              <button
                type="button"
                onClick={() => saveClassifications({ continueAfterSave: true })}
                disabled={savingClassifications}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400"
              >
                {savingClassifications ? "Saving..." : "Save And Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div id="print-modal-content" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                backgroundColor: 'white',
                width: '90%',
                height: '90%',
                display: 'flex',
                flexDirection: 'column',
                padding: '1rem'
            }}>
                <div className="pdf-viewer-controls" style={{ paddingBottom: '10px', borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold' }}>Print Preview</span>
                    <div>
                      <button onClick={handlePrint} style={{
                        padding: '5px 10px',
                        cursor: 'pointer',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        marginRight: '10px'
                      }}>Print</button>
                      <button onClick={() => {
                          window.URL.revokeObjectURL(previewUrl);
                          setPreviewUrl('');
                      }} style={{
                        padding: '5px 10px',
                        cursor: 'pointer',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }}>Close</button>
                    </div>
                </div>
                <div style={{ flex: 1, overflow: 'hidden', paddingTop: '1rem' }}>
                  <PDFViewer file={previewUrl} />
                </div>
            </div>
        </div>
    )}
    </>
  );
};

export default GameDay;
